import React, { useState } from 'react';
import { Package, Coffee, History, BarChart3, Tag, Menu, X, Layers, Settings } from 'lucide-react';
import RawMaterials from '../inventory/RawMaterials';
import MenuItems from '../inventory/MenuItems';
import InventoryTransactions from '../inventory/InventoryTransactions';
import InventoryAnalytics from '../inventory/InventoryAnalytics';
import CategoryManager from '../inventory/CategoryManager';
import AddOnCategories from '../inventory/AddOnCategories';
import AddOnOptions from '../inventory/AddOnOptions';

const Inventory: React.FC = () => {
  const [activeTab, setActiveTab] = useState('menuItems');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileMenuClosing, setIsMobileMenuClosing] = useState(false);

  const tabs = [
    {
      id: 'menuItems',
      name: 'Menu Items',
      icon: Coffee,
      description: 'Manage cafe menu and availability',
      shortName: 'Menu'
    },
    {
      id: 'rawMaterials',
      name: 'Raw Materials',
      icon: Package,
      description: 'Manage raw materials and supplies',
      shortName: 'Materials'
    },
    {
      id: 'categories',
      name: 'Categories',
      icon: Tag,
      description: 'Manage categories for items and materials',
      shortName: 'Categories'
    },
    {
      id: 'addOnCategories',
      name: 'Add-On Categories',
      icon: Layers,
      description: 'Manage add-on categories and sub-categories',
      shortName: 'Add-Ons'
    },
    {
      id: 'addOnOptions',
      name: 'Add-On Options',
      icon: Settings,
      description: 'Manage individual add-on options and pricing',
      shortName: 'Options'
    },
    {
      id: 'transactions',
      name: 'Transactions',
      icon: History,
      description: 'View inventory transaction history',
      shortName: 'History'
    },
    {
      id: 'analytics',
      name: 'Analytics',
      icon: BarChart3,
      description: 'Inventory insights and reports',
      shortName: 'Analytics'
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'rawMaterials':
        return <RawMaterials />;
      case 'menuItems':
        return <MenuItems />;
      case 'categories':
        return <CategoryManager />;
      case 'addOnCategories':
        return <AddOnCategories />;
      case 'addOnOptions':
        return <AddOnOptions />;
      case 'transactions':
        return <InventoryTransactions />;
      case 'analytics':
        return <InventoryAnalytics />;
      default:
        return <RawMaterials />;
    }
  };

  const activeTabData = tabs.find(tab => tab.id === activeTab);

  const handleMobileMenuToggle = () => {
    if (isMobileMenuOpen) {
      setIsMobileMenuClosing(true);
      setTimeout(() => {
        setIsMobileMenuOpen(false);
        setIsMobileMenuClosing(false);
      }, 300);
    } else {
      setIsMobileMenuOpen(true);
    }
  };

  const handleTabSelect = (tabId: string) => {
    setActiveTab(tabId);
    if (isMobileMenuOpen) {
      handleMobileMenuToggle();
    }
  };

  return (
    <div className="flex flex-col h-full max-h-screen overflow-hidden">
      {/* Mobile Header with Hamburger Menu */}
      <div className="lg:hidden bg-white/90 dark:bg-gray-800/90 backdrop-blur-md border-b border-gray-200 dark:border-gray-700 sticky top-0 z-20 relative">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {activeTabData && (
              <>
                <activeTabData.icon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                <div>
                  <h1 className="text-lg font-semibold text-gray-900 dark:text-white">{activeTabData.name}</h1>
                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{activeTabData.description}</p>
                </div>
              </>
            )}
          </div>
          <button
            onClick={handleMobileMenuToggle}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-gray-600 dark:text-gray-400" />
            ) : (
              <Menu className="w-6 h-6 text-gray-600 dark:text-gray-400" />
            )}
          </button>
        </div>

        {/* Mobile Dropdown Menu */}
        {isMobileMenuOpen && (
          <div className={`absolute top-full left-0 right-0 bg-white/95 dark:bg-gray-800/95 backdrop-blur-md border-b border-gray-200 dark:border-gray-700 shadow-lg z-30 ${
            isMobileMenuClosing ? 'mobile-dropdown-exit' : 'mobile-dropdown-enter'
          }`}>
            <div className="max-h-64 overflow-y-auto">
              {tabs.map((tab, index) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabSelect(tab.id)}
                    className={`w-full px-4 py-4 flex items-center space-x-3 text-left transition-colors mobile-menu-item ${
                      activeTab === tab.id
                        ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border-r-4 border-primary-500'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                    style={{ animationDelay: `${0.1 + index * 0.05}s` }}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">{tab.name}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 truncate">{tab.description}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Desktop Tab Navigation */}
      <div className="hidden lg:block border-b border-gray-200 dark:border-gray-700 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md sticky top-0 z-10">
        <div className="px-6">
          <nav className="flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm xl:text-base flex items-center space-x-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Tablet Tab Navigation */}
      <div className="hidden md:block lg:hidden border-b border-gray-200 dark:border-gray-700 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md sticky top-0 z-10">
        <div className="px-4">
          <nav className="grid grid-cols-5 gap-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-3 px-2 flex flex-col items-center justify-center space-y-1 text-xs font-medium transition-colors border-b-2 ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="truncate w-full text-center">{tab.shortName}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto overflow-x-hidden">
          <div className="p-4 md:p-6 lg:p-8 w-full min-w-0">
            {renderTabContent()}
          </div>
        </div>
      </div>

      {/* Mobile Bottom Navigation (Alternative - uncomment if preferred) */}
      {/* 
      <div className="sm:hidden bg-white border-t border-gray-200 px-2 py-2 flex items-center justify-around sticky bottom-0 z-10">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-col items-center justify-center py-2 px-3 rounded-lg transition-colors min-w-0 flex-1 ${
                activeTab === tab.id
                  ? 'text-primary-600 bg-primary-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Icon className="w-5 h-5 mb-1" />
              <span className="text-xs font-medium truncate w-full text-center">{tab.shortName}</span>
            </button>
          );
        })}
      </div>
      */}
    </div>
  );
};

export default Inventory; 